import React, { useState } from 'react';
import { Box, Typography, makeStyles, Theme, Paper, useTheme } from '@material-ui/core';
import { PieChart as RechartsPieChart, Pie, Cell, Sector, Tooltip } from 'recharts';

interface RoomStat {
  roomName: string;
  type: string;
  percentage: number;
  duration: number;
  cost: number;
}

interface StatsPieChartProps {
  stats: RoomStat[];
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    marginTop: theme.spacing(2),
    width: '100%',
    background: 'transparent',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 200px)',
  },
  chartContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(6),
    flex: '1',
  },
  chartWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '500px',
    height: '500px',
    flexShrink: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    borderRadius: theme.spacing(2),
    border: '1px solid rgba(255, 255, 255, 0.05)',
    padding: theme.spacing(2),
  },
  legendContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    flex: '1',
    maxHeight: '500px',
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '3px',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.3)',
      },
    },
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2.5),
    borderRadius: theme.spacing(1.5),
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
  },
  legendDot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    flexShrink: 0,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  legendText: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '14px',
    fontWeight: 500,
    flex: '1',
    letterSpacing: '0.2px',
  },
  legendDetails: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
    minWidth: '80px',
  },
  legendPercentage: {
    color: theme.palette.common.white,
    fontSize: '16px',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  legendTime: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '13px',
    fontWeight: 400,
  },
  legendCost: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '13px',
    fontWeight: 400,
  },
}));

const getRoomColor = (type: string): string => {
  switch (type?.toUpperCase()) {
    case 'OR':
    case 'OPERATING_ROOM':
      return '#74AC48';

    case 'PRE_OP':
    case 'PRE-OP':
    case 'PREOP':
      return '#FFBB28';

    case 'POST_OP':
    case 'POST-OP':
    case 'POSTOP':
      return '#3D5A80';

    case 'PACU':
      return '#2B3C87';

    case 'BREAK_ROOM':
    case 'BREAK':
      return '#3D5A80';

    case 'WAITING_ROOM':
    case 'WAITING':
      return '#00A7F7';

    case 'UNKNOWN':
    case 'OFF_SITE':
      return '#9E9E9E';

    default:
      return '#F38181';
  }
};

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${remainingMinutes}m`;
};

const CustomTooltipPie = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isOuterRing = data.name === 'Total On Site' || data.name === 'Total Off Site';

    return (
      <Paper>
        <Box p={2}>
          <Typography gutterBottom>{data.label}</Typography>
          <Box display="flex" alignItems="center" style={{ width: '16ch' }}>
            <Typography variant="h4">{Math.round(payload[0].value)} m</Typography>
            {isOuterRing && data.percentage && (
              <Typography variant="body2" style={{ marginLeft: '8px', color: 'rgba(255, 255, 255, 0.7)' }}>
                ({data.percentage}%)
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    );
  }
  return null;
};

const StatsPieChart: React.FC<StatsPieChartProps> = ({ stats }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [selection, setSelection] = useState<number | undefined>();

  const filteredStats = stats.filter(stat => stat.percentage > 0);

  const roomTypeOrder = {
    WAITING_ROOM: 1,
    WAITING: 1,
    PRE_OP: 2,
    'PRE-OP': 2,
    PREOP: 2,
    OR: 3,
    OPERATING_ROOM: 3,
    PACU: 4,
    POST_OP: 5,
    'POST-OP': 5,
    POSTOP: 5,
    VIRTUAL: 6,
    UNKNOWN: 7,
    OFF_SITE: 7,
  };

  const sortedStats = filteredStats.sort((a, b) => {
    const orderA = roomTypeOrder[a.type?.toUpperCase()] || 999;
    const orderB = roomTypeOrder[b.type?.toUpperCase()] || 999;
    return orderA - orderB;
  });

  const onPieEnter = (_, index) => {
    setSelection(index);
  };

  const onPieLeave = () => {
    setSelection(undefined);
  };

  const renderActiveShape = props => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
      </g>
    );
  };

  const data = sortedStats.map((stat, index) => ({
    label: stat.roomName,
    value: stat.duration,
    color: getRoomColor(stat.type),
    percentage: stat.percentage,
    cost: stat.cost,
    type: stat.type,
  }));

  const totalDuration = data.reduce((acc, item) => acc + item.value, 0);

  const onSiteData: Array<{
    name: string;
    label: string;
    value: number;
    color: string;
    percentage?: string;
  }> = [
    {
      name: 'Total On Site',
      label: 'Total On Site',
      value: data.reduce((acc, item) => {
        const type = item.type?.toUpperCase();
        return type !== 'UNKNOWN' ? acc + item.value : acc;
      }, 0),
      color: '#2E8B57',
    },
    {
      name: 'Total Off Site',
      label: 'Total Off Site',
      value: data.reduce((acc, item) => {
        const type = item.type?.toUpperCase();
        return type === 'UNKNOWN' ? acc + item.value : acc;
      }, 0),
      color: '#6B7280',
    },
  ];

  onSiteData.forEach(item => {
    item.percentage = totalDuration > 0 ? ((item.value / totalDuration) * 100).toFixed(1) : '0.0';
  });

  if (filteredStats.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography style={{ color: 'rgba(255, 255, 255, 0.6)' }}>No data available for pie chart</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.chartContainer}>
        <Box className={classes.chartWrapper}>
          <RechartsPieChart width={450} height={450}>
            <Pie
              data={onSiteData}
              cx="50%"
              cy="50%"
              innerRadius={190}
              outerRadius={200}
              fill="#8884d8"
              stroke="none"
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              paddingAngle={1}
            >
              {onSiteData.map((entry, index) => (
                <Cell
                  key={`outer-cell-${index}`}
                  fill={entry.color}
                  style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}
                />
              ))}
            </Pie>

            <Pie
              data={data}
              cx="50%"
              cy="50%"
              activeIndex={selection}
              activeShape={selection !== undefined ? renderActiveShape : undefined}
              innerRadius={100}
              outerRadius={180}
              fill="#8884d8"
              stroke="none"
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              paddingAngle={1}
              cornerRadius={0}
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
              labelLine={false}
              label={props => {
                if (Math.abs(props.endAngle - props.startAngle) < 10) {
                  return null;
                }

                const innerRadius = props.innerRadius;
                const outerRadius = props.outerRadius;
                const cx = props.cx;
                const cy = props.cy;
                const midAngle = props.midAngle;

                const RADIAN = Math.PI / 180;
                const radius = innerRadius + (outerRadius - innerRadius) * 0.5;

                return (
                  <text
                    x={cx + radius * Math.cos(-midAngle * RADIAN)}
                    y={cy + radius * Math.sin(-midAngle * RADIAN)}
                    fontSize={12}
                    fontWeight={600}
                    fill="white"
                    textAnchor="middle"
                    dominantBaseline="central"
                    style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))' }}
                  >
                    {`${(props.percent * 100).toFixed(0)}%`}
                  </text>
                );
              }}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}
                />
              ))}
            </Pie>
            <text
              x="50%"
              y="50%"
              fontSize={20}
              fontWeight={600}
              fill="white"
              textAnchor="middle"
              dominantBaseline="auto"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))' }}
            >
              {Math.round(totalDuration)}
            </text>
            <text
              x="50%"
              y="53%"
              fontSize={14}
              fontWeight={500}
              fill="rgb(125,163,206)"
              textAnchor="middle"
              dominantBaseline="hanging"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5))' }}
            >
              minutes
            </text>
            <Tooltip
              content={({ active, payload, label }) => (
                <CustomTooltipPie active={active} payload={payload} label={label} />
              )}
              cursor={{ fill: 'rgba(255,255,255,0.15)' }}
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 4,
                border: 0,
              }}
            />
          </RechartsPieChart>
        </Box>

        <Box className={classes.legendContainer}>
          <Box mb={2} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '16px' }}>
            {onSiteData.map((item, index) => (
              <Box
                key={item.name}
                className={classes.legendItem}
                style={{ marginBottom: index < onSiteData.length - 1 ? '16px' : '0' }}
              >
                <div className={classes.legendDot} style={{ backgroundColor: item.color }} />
                <Typography className={classes.legendText}>{item.name}</Typography>
                <Box className={classes.legendDetails}>
                  <Typography className={classes.legendPercentage}>
                    {((item.value / totalDuration) * 100).toFixed(1)}%
                  </Typography>
                  <Typography className={classes.legendTime}>{formatDuration(item.value)}</Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {sortedStats.map((stat, index) => (
            <Box key={stat.roomName} className={classes.legendItem}>
              <div className={classes.legendDot} style={{ backgroundColor: getRoomColor(stat.type) }} />
              <Typography className={classes.legendText}>{stat.roomName}</Typography>
              <Box className={classes.legendDetails}>
                <Typography className={classes.legendPercentage}>{stat.percentage}%</Typography>
                <Typography className={classes.legendTime}>{formatDuration(stat.duration)}</Typography>
                <Typography className={classes.legendCost}>${stat.cost}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default StatsPieChart;
